import { nanoid } from 'nanoid';

export const WINDOW_MANAGER_CHANNEL = 'deon-window-manager';

export const WM_MSG = {
    REGISTER: 'register',
    HEARTBEAT: 'heartbeat',
    UNREGISTER: 'unregister',
    REQUEST_ROSTER: 'request-roster',
    CLOSE_ALL: 'close-all',
    CLOSE: 'close',
    FOCUS: 'focus',
    FOCUS_ALL: 'focus-all'
};

export const WM_HEARTBEAT_INTERVAL = 3000;
export const WM_STALE_TIMEOUT = 60000;

const WINDOW_ID_KEY = 'wmWindowId';
const WINDOW_OPENED_AT_KEY = 'wmOpenedAt';

export const isBroadcastSupported = () => typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined';

export const createWindowManagerChannel = () => {
    if (!isBroadcastSupported()) return null;
    return new BroadcastChannel(WINDOW_MANAGER_CHANNEL);
};

export const closeAllChildWindows = () => {
    const channel = createWindowManagerChannel();
    if (!channel) return;
    try {
        channel.postMessage({ type: WM_MSG.CLOSE_ALL });
    } catch (e) {
        console.log('close all message error-->', e);
    }
};

export const isChildPopup = () => {
    if (typeof window === 'undefined' || !window.opener) return false;
    try {
        return !window.opener.closed;
    } catch (e) {
    // Cross-origin opener still implies this is a popup.
        return true;
    }
};

export const getOrCreateWindowId = () => {
    if (typeof window === 'undefined') return null;
    let id = sessionStorage.getItem(WINDOW_ID_KEY);
    if (!id) {
        id = `win_${nanoid(8)}`;
        sessionStorage.setItem(WINDOW_ID_KEY, id);
    }
    return id;
};

export const getOrCreateWindowOpenedAt = () => {
    if (typeof window === 'undefined') return Date.now();
    let openedAt = Number(sessionStorage.getItem(WINDOW_OPENED_AT_KEY));
    if (!openedAt) {
        openedAt = Date.now();
        sessionStorage.setItem(WINDOW_OPENED_AT_KEY, String(openedAt));
    }
    return openedAt;
};

export const bumpWindowOpenedAt = () => {
    const now = Date.now();
    if (typeof window !== 'undefined') sessionStorage.setItem(WINDOW_OPENED_AT_KEY, String(now));
    return now;
};

export const setChildWindowMeta = (meta) => {
    if (typeof window === 'undefined') return;
    const command = meta?.command || '';
    const line2 = meta?.line2 || '';
    window.__deonCmdMeta = command || line2 ? { command, line2 } : null;
};

export const getChildWindowMeta = () => {
    if (typeof window === 'undefined') return null;
    return window.__deonCmdMeta || null;
};

const isServiceWorkerSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

// Register the focus-only service worker (prototype). Lets any tab focus an
// existing child window via the SW Clients API, without holding a ref.
export const registerFocusServiceWorker = () => {
    if (typeof window === 'undefined' || !isServiceWorkerSupported()) return;
    try {
        navigator.serviceWorker.register('/sw.js').catch((e) => console.log('sw register error-->', e));
    } catch (e) {
        console.log('sw register error-->', e);
    }
};

// Ask the service worker to bring the given window URL(s) to the front. Must be
// called from a user gesture so the Notification permission request (the SW's
// fallback when a direct focus is blocked) is allowed.
export const requestChildWindowFocus = (urls, meta = {}) => {
    if (typeof window === 'undefined' || !isServiceWorkerSupported()) return false;
    const list = (urls || []).filter(Boolean);
    if (!list.length) return false;
    try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    } catch (e) {
        console.log('notification permission error-->', e);
    }
    const { controller } = navigator.serviceWorker;
    if (!controller) return false;
    try {
        controller.postMessage({
            type: 'WM_FOCUS',
            urls: list,
            title: meta.title || 'Focus window',
            body: meta.body || ''
        });
        return true;
    } catch (e) {
        console.log('sw focus message error-->', e);
        return false;
    }
};

const openedWindows = new Map();

const SKIP_NAMES = new Set(['', '_blank', '_self', '_parent', '_top']);

export const getTrackedWindow = (name) => {
    if (!name) return null;
    const ref = openedWindows.get(name);
    if (!ref || ref.closed) {
        openedWindows.delete(name);
        return null;
    }
    return ref;
};

export const installWindowOpenTracker = () => {
    if (typeof window === 'undefined' || window.__wmOpenTracked) return;
    const nativeOpen = window.open.bind(window);
    window.open = (url, name, ...rest) => {
        const ref = nativeOpen(url, name, ...rest);
        if (ref) {
            try {
                // New windows inherit a copy of the opener's sessionStorage;
                // clear the identity keys so they mint their own id. The
                // about:blank check skips refs to already-running windows
                // (focus re-targets, '_self' navigations).
                if (ref.location.href === 'about:blank') {
                    ref.sessionStorage.removeItem(WINDOW_ID_KEY);
                    ref.sessionStorage.removeItem(WINDOW_OPENED_AT_KEY);
                }
            } catch (e) {
                // Cross-origin window — not our app, nothing to strip.
            }
            if (typeof name === 'string' && !SKIP_NAMES.has(name)) {
                openedWindows.set(name, ref);
            }
        }
        return ref;
    };
    window.__wmOpenTracked = true;
};

export const focusWindowByName = (name) => {
    if (typeof window === 'undefined' || !name || SKIP_NAMES.has(name)) return false;
    try {
        const ref = window.open('', name);
        if (ref && !ref.closed) {
            if (ref.location.href === 'about:blank') {
                ref.close();
                return false;
            }
            openedWindows.set(name, ref);
            ref.focus();
            return true;
        }
    } catch (e) {
        console.log('focus window error-->', e);
    }
    return false;
};
